import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {SidebarSize} from '../../models/sidebar-size';
import {PushNotificationSettings} from '../../models/signalr-query-result.interface';
import {
  AddToastMessage,
  LoginSuccess,
  GetServerConnectionStatus,
  ToggleAppTheme,
  ToggleSidebar,
  GetLanguages
} from './shared.actions';
import {NotificationService} from '../../services/notifications.service';
import {Injectable} from '@angular/core';
import {UpdateAccessPoints} from '../access-point/access-point.actions';
import {AccessPointStatusSignalR, ToastNotification} from '../../models/notification-payload';
import {LanguageSettings, SharedStateModel} from './shared.model';
import {filter, map} from 'rxjs/operators';
import {UpdateNetworks} from '../networks/networks.actions';
import {NetworkStatusSignalR, PanelTestSignalR} from '../../models/network/network.interface';
import {Subject} from 'rxjs';
import * as moment from 'moment';
import {UpdateRealTimeEvents, UpdateSelectedEvent} from '../events/events.actions';
import {EventsSelectors} from '../events/events.selectors';
import {EventAlert} from '../../models/events/event-interface';
import { LockdownGroupMessageSignalR, LockdownModelMessageSignalR } from '../../models/lockdown/lockdown';
import { UpdateLockdowns, UpdateLockdownsByAccessPointStatus, UpdateSelectedLockdown } from '../lockdown/lockdown.actions';
import { TicksToDateService } from '../../services/ticks-to-date.service';
import { CommonService } from '../../services/common.service';
import {EventsService} from '../../services/events.service';

const CHUNK_SIZE: number = 20;


@State<SharedStateModel>({
  name: 'shared',
  defaults: {
    isDarkTheme: true,
    sidebarSize: SidebarSize.FULL,
    notificationSettings: null,
    pagesChunkSize: CHUNK_SIZE,
    statusChannelSubscription$: new Subject<any>(),
    toastMessage: null,
    panelTestSubject$: new Subject<PanelTestSignalR>(),
    serverConnectionStatus: 'Connected',
    languageSettings: null,
    newAlert: null,
  }
})
@Injectable()

export class SharedState {
  constructor(
    private store: Store,
    private notificationService: NotificationService,
    private eventsService: EventsService,
    private ticksToDateService: TicksToDateService,
    private commonService: CommonService
  ) {}

  @Selector([SharedState])
  static statusChannelSubscription(state: SharedStateModel): Subject<any> {
    return state.statusChannelSubscription$;
  }

  @Selector([SharedState])
  static panelTestSubject(state: SharedStateModel): Subject<PanelTestSignalR> {
    return state.panelTestSubject$;
  }

  @Selector([SharedState])
  static resultsChunkSize(state: SharedStateModel): number {
    return state.pagesChunkSize;
  }

  @Selector([SharedState])
  static isDarkTheme(state: SharedStateModel): boolean {
    return state.isDarkTheme;
  }

  @Selector([SharedState])
  static sidebarSize(state: SharedStateModel): SidebarSize {
    return state.sidebarSize;
  }

  @Selector([SharedState])
  static toastMessage(state: SharedStateModel): ToastNotification {
    return state.toastMessage;
  }

  @Selector([SharedState])
  static notificationSettings(state: SharedStateModel) {
    return state.notificationSettings;
  }

  @Selector([SharedState])
  static serverConnectionStatus(state: SharedStateModel): string {
    return state.serverConnectionStatus;
  }
  @Selector([SharedState])
  static languageSettings(state: SharedStateModel): LanguageSettings {
    return state.languageSettings;
  }
  @Selector([SharedState])
  static newAlert(state: SharedStateModel): EventAlert {
    return state.newAlert;
  }


  @Action(GetServerConnectionStatus)
  async getServerConnectionStatus(ctx: StateContext<SharedStateModel>, action: GetServerConnectionStatus) {
    let serverStatus = await this.commonService.getServerConnectionStatus();
    return ctx.patchState({serverConnectionStatus: serverStatus ? serverStatus : 'BackendDisconnected'});
  }

  @Action(ToggleAppTheme)
  toggleAppTheme(ctx: StateContext<SharedStateModel>, action: ToggleAppTheme) {
    ctx.patchState({isDarkTheme: action.paylaod});
  }

  @Action(ToggleSidebar)
  toggleSidbar(ctx: StateContext<SharedStateModel>) {
    const newSize = ctx.getState().sidebarSize === SidebarSize.SMALL ? SidebarSize.FULL : ctx.getState().sidebarSize - 1;
    ctx.patchState({sidebarSize: newSize});
  }

  @Action(GetLanguages)
  async getLanguages(ctx: StateContext<SharedStateModel>) {
    const languageSettings = await this.commonService.getLanguageSettings();
    ctx.patchState({languageSettings});
  }

  @Action(LoginSuccess)
  async setNotificationSettings(ctx: StateContext<SharedStateModel>, action: LoginSuccess) {
    await this.notificationService.connectToNotifications(
      (settings: PushNotificationSettings) => {ctx.patchState({notificationSettings: settings});},
      () => ctx.getState().notificationSettings);

    this.notificationService
      .channel<AccessPointStatusSignalR>('status')
      .pipe(filter(payload => !!payload))
      .subscribe(payload => {
        ctx.dispatch(new UpdateAccessPoints(payload));
        ctx.getState().statusChannelSubscription$.next(payload);
        ctx.dispatch(new UpdateLockdownsByAccessPointStatus(payload));

        let selectedEvent = this.store.selectSnapshot(EventsSelectors.selectedEvent);

        const locationId = selectedEvent?.eventData?.location ?
              selectedEvent?.location?.panelId?.toString() + '_' + selectedEvent?.location?.id
              : null;
        if ( locationId === payload.accessPoints[0].id) {

          selectedEvent.location.isSecured = payload.accessPoints[0].isSecured;
          selectedEvent.location.isLockdown = payload.accessPoints[0].isLockdown;
          selectedEvent.location.isOpen = payload.accessPoints[0].isOpen;

          ctx.dispatch(new UpdateSelectedEvent(selectedEvent));

        }

      });

    this.notificationService
      .channel<NetworkStatusSignalR>('entitiesChanged')
      .pipe(filter(payload => !!payload))
      .subscribe(payload => {
        ctx.dispatch(new UpdateNetworks(payload));
      });

    this.notificationService
      .channel<LockdownGroupMessageSignalR>('lockdownChanges-lockdownGroup')
      .pipe(filter(payload => !!payload))
      .subscribe(payload => {
        ctx.dispatch(new UpdateLockdowns(payload));
      });

    this.notificationService
      .channel<LockdownModelMessageSignalR>('lockdownChanges-lockdownModel')
      .pipe(filter(payload => !!payload))
      .subscribe(payload => {
        ctx.dispatch(new UpdateSelectedLockdown(payload));
      });

    this.notificationService
      .channel<any>('events')
      .pipe(filter(payload => !!payload))
      .subscribe(payload => {
        const newEvent: EventAlert = {
          id: Math.random().toString().slice(2),
          eventTypeId: payload.EventType,
          name: payload.Name,
          location: payload.Location,
          generator: payload.Generator,
          timeFrom: payload.Time,
          alert: null,
          alarm: payload.Alarm ? this.prepareAlertData(payload.Alarm) : null,
          description: '',
          eventTypeName: '',
          time: payload.Time,
          event: {
            eventGeneratorId: payload.Event?.EventGeneratorId,
            eventLocationId: payload.Event?.EventLocationId
          },
        };

        // let realTimeEvents = this.store.selectSnapshot(EventsSelectors.realTimeEvents);

        // realTimeEvents.unshift(newEvent);

        ctx.dispatch(new UpdateRealTimeEvents([newEvent]));
        if(+newEvent.eventTypeId === 3) { // Works just for Alerts
          ctx.patchState({newAlert: newEvent});
          this.eventsService.onScreenDialog.next(true);
        }

      });

    this.notificationService.channel<PanelTestSignalR>('panelStatusChange')
      .pipe(
        filter(payload => !!payload),
        map(data => {
          delete data.topic;
          let date = this.ticksToDateService.ticksToDate(data.time);
          data.time = moment(date).format('DD/MM/YYYY HH:mm:ss');
          return data;
      })).subscribe(payload => {
        ctx.getState().panelTestSubject$.next(payload);
      });
  }

  private prepareAlertData(obj){
    obj = this.objectFieldsFirstLaterToLowerCase(obj);
    if(!obj.hasOwnProperty('remarkInfo') && obj.hasOwnProperty('alarmRemarkInfo')){
      obj.remarkInfo = obj.alarmRemarkInfo;
      delete obj.alarmRemarkInfo;
    }
    return obj;
  }
  private objectFieldsFirstLaterToLowerCase(obj:object):object {
    let newObj = {};
    for (const [key, value] of Object.entries(obj)) {
      newObj[this.firstLetterToLowerCase(key)] = (typeof value === 'object') ? this.objectFieldsFirstLaterToLowerCase(value) : value;
    }
    return newObj;
  }
  private firstLetterToLowerCase(string:string):string {
    return string.charAt(0).toLowerCase() + string.slice(1);
  }

  //USE this action to send toast message that will be displayed crosswide the entire app
  //payload must contain "severity": the type of message, will determine the styling it gets.
  //there are four type of severity: info , warning, error, success
  //second argument is "summary": the title that will be displayed
  //last argument is "details": the content of the message
  @Action(AddToastMessage)
  async addToastMessage(ctx: StateContext<SharedStateModel>, action: AddToastMessage) {
    ctx.patchState({toastMessage: action.paylaod});
  }
}
